<template>
  <poppins-dropdown
    :id="id"
    :labelName="labelName"
    :options="options"
    :value="dropdownValue"
    :status="status"
    :helperText="helperText"
    :required="required"
    :disabled="disabled"
    class="dropdown-icon-overridden"
    name="timePickerDropdown"
    @input="dropdownSelectionChanged"
  >
    <icon-time class="dropdown-icon" />
  </poppins-dropdown>
</template>

<script lang="ts">
import Vue from 'vue';
import { addMinutes, format, formatISO, isBefore, isSameDay, parseISO } from 'date-fns';
import Dropdown from '../dropdown/Dropdown.vue';
import SelectionOption from '../../helpers/selection';
import helperStatus from '../../helpers/status';
import IconTime from '../../icons/IconTime.vue';

/**
 * TimePicker provides a consistent way for us to allow users to choose times. It encapsulates the logic to generate the options and display text.
 * TimePicker is essentially a specialized Dropdown wrapper.
 * You can find it used in Search filters at https://www.sittercity.com/availability/search?zipcode=60601&careType=babysitter and in the Job post flow at https://www.sittercity.com/job-post/schedule.
 * TimePicker should be getting a Date, but converting it to String for its child component, Dropdown.
 */

export default Vue.extend({
  name: 'TimePicker',
  components: {
    PoppinsDropdown: Dropdown,
    IconTime,
  },
  props: {
    // accessibility
    /**
     * Label will move into the placeholder slot if there is no value
     */
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    /**
     * Id is necessary for screenreader to be able to read the label
     */
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    // values
    /**
     * Start is passed in as a full timestamp that includes the date, but is usually displayed as HH:MM am/pm. You can access the day's date, though.
     */
    start: {
      type: Date,
      default: null,
    },
    /**
     * End is passed in as a full timestamp that includes the date, but is usually displayed as HH:MM am/pm. You can access the day's date, though.
     * End input should never display times that happen before the start time.
     */
    end: {
      type: Date,
      default: null,
    },
    /**
     * Value is usually a full timestamp string that includes the date.
     */
    value: {
      type: Date,
      default: null,
    },
    /**
     * Times are usually generated in 30 minute-increments
     */
    step: {
      type: Number,
      default: 30,
    },
    // validation
    /**
     * Status determines whether TimePicker is in error state or not.
     */
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    /**
     * Helper text displays what the error message is
     */
    helperText: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * Selection array displays label at the top of the list to give the user some context.
       * It has separate display and value properties because that's what SelectionOption array expects.
       */
      firstSelectionOption: {
        display: this.labelName,
        value: this.labelName,
      },
    };
  },
  computed: {
    /**
     * This is the value being passed into the child component, Dropdown
     */
    dropdownValue(): string {
      if (this.value) {
        return formatISO(this.value);
      }

      return '';
    },
    /**
     * Generates Dropdown times
     */
    options(): SelectionOption[] {
      const times: Array<SelectionOption> = [];

      // Selection array displays label at the top of the list to give the user some context
      times.push(this.firstSelectionOption);

      // return empty selection if we can't generate a time range
      if (!this.start || !this.end) return times;

      let t = this.start;

      while (isBefore(t, this.end)) {
        const formattedTime = format(t, 'h:mm aaa');
        if (isSameDay(t, this.start)) {
          times.push({
            display: formattedTime,
            value: formatISO(t),
          });
        } else {
          times.push({
            display: `${formattedTime} (next day)`,
            value: formatISO(t),
          });
        }
        t = addMinutes(t, this.step);
      }

      return times;
    },
  },
  methods: {
    dropdownSelectionChanged(newValue: string) {
      if (newValue) {
        // if user selected dropdown default option, reset value back to empty
        // this setup is due to the need for the label to move into the placeholder slot if selection is empty
        if (newValue === this.labelName) {
          this.$emit('input', null);
          return;
        }

        // dropdownValue is a string for Dropdown, but needs to be a Date for its parent components.
        const stringToDate = parseISO(newValue);

        this.$emit('input', stringToDate);
      }
    },
  },
});
</script>
